import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, getErrorMessage } from "@/lib/api";
import { formatFee } from "@/lib/candidate-utils";
import { ArrowLeft, Buildings, PencilSimple, Trash, X, CheckCircle, Calendar, EnvelopeSimple, Phone, MapPin, Note, Plus, Star, StarFour, UserCircle, ClockCounterClockwise, Sparkle, PaperPlaneTilt, UsersThree } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_STYLE = {
  active: "bg-zinc-100 text-zinc-700",
  placed: "bg-emerald-100 text-emerald-800",
  lost: "bg-red-100 text-[#B91C1C]",
  countered: "bg-amber-100 text-amber-800",
};

const OUTCOME_LABEL = {
  "": "—",
  waiting: "Waiting",
  offered: "Offered",
  rejected: "Rejected",
  no_show: "No-show",
};

function _relTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [timelineFilter, setTimelineFilter] = useState("all");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${id}`);
      setClient(data);
    } catch (e) {
      setError(getErrorMessage(e));
    }
  }, [id]);

  const refreshTimeline = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${id}/timeline`);
      setTimeline(data.events || []);
    } catch (e) { /* silent — timeline is a secondary view */ }
  }, [id]);

  useEffect(() => { refresh(); refreshTimeline(); }, [refresh, refreshTimeline]);
  useEffect(() => { refreshTimeline(); }, [refreshTimeline, client?.updated_at]);

  const remove = async () => {
    if (!window.confirm(`Delete "${client?.name}"? Existing candidate history is kept, but this client card will be removed.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/clients/${id}`);
      navigate("/clients");
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setDeleting(false);
    }
  };

  if (error) return <div className="border border-[#FF2A2A] bg-red-50 text-[#B91C1C] p-4 rounded-sm text-sm">{error}</div>;
  if (!client) return <div className="text-zinc-500 text-sm uppercase tracking-[0.2em]">Loading</div>;

  const s = client.stats || {};

  return (
    <div className="space-y-8" data-testid="client-detail-page">
      <Link to="/clients" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-950 transition-colors">
        <ArrowLeft size={14} weight="bold" /> All clients
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-1.5">
            <Buildings size={12} weight="bold" /> Client
          </div>
          <h1 className="font-heading font-black tracking-tight text-4xl sm:text-5xl leading-none break-words">
            {client.name}
          </h1>
          <div className="text-sm text-zinc-500 mt-4 flex flex-wrap gap-x-4 gap-y-1">
            {client.phone && <span className="inline-flex items-center gap-1"><Phone size={12} weight="bold" /> {client.phone}</span>}
            {client.address && <span className="inline-flex items-center gap-1"><MapPin size={12} weight="bold" /> {client.address}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setEditing(true)} data-testid="edit-client-btn" className="inline-flex items-center gap-1.5 text-sm border border-zinc-200 bg-white text-zinc-950 px-4 py-2 rounded-sm hover:bg-zinc-100">
            <PencilSimple size={14} weight="bold" /> Edit
          </button>
          {user?.role === "admin" && (
            <button onClick={remove} disabled={deleting} data-testid="delete-client-btn" className="inline-flex items-center gap-1.5 text-sm border border-[#FF2A2A] text-[#B91C1C] px-4 py-2 rounded-sm hover:bg-red-50 disabled:opacity-50">
              <Trash size={14} weight="bold" /> Delete
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard label="Candidates sent" value={s.sent || 0} />
        <StatCard label="Active" value={s.active || 0} />
        <StatCard label="Placed" value={s.placed || 0} accent="emerald" />
        <StatCard label="Win rate" value={`${s.win_rate || 0}%`} accent={s.win_rate >= 50 ? "emerald" : "amber"} />
        <StatCard label="Fees written" value={formatFee(s.fees_written || 0)} accent="emerald" small />
      </div>

      {/* Contacts */}
      <ContactsSection clientId={id} contacts={client.contacts || []} onChange={refresh} />

      {/* Notes */}
      {client.notes && (
        <div className="bg-white border border-zinc-200 rounded-sm p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3 flex items-center gap-1.5">
            <Note size={12} weight="bold" /> Notes
          </div>
          <div className="text-sm whitespace-pre-wrap text-zinc-800 leading-relaxed">{client.notes}</div>
        </div>
      )}

      {/* Candidates */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
          Every candidate we've sent
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
          {(client.candidates || []).length === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-500">
              No candidates yet. As soon as you type "{client.name}" on an interview slot, they'll show up here.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                <div className="col-span-4">Candidate</div>
                <div className="col-span-3">Role</div>
                <div className="col-span-2">Recruiter(s)</div>
                <div className="col-span-1 text-right">Fee</div>
                <div className="col-span-2 text-right">Status</div>
              </div>
              {(client.candidates || []).map((c) => (
                <Link
                  key={c.id}
                  to={`/candidates/${c.id}`}
                  data-testid={`client-cand-${c.id}`}
                  className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 last:border-0 items-center hover:bg-zinc-50 transition-colors text-sm"
                >
                  <div className="col-span-4 font-medium truncate">{c.name}</div>
                  <div className="col-span-3 text-zinc-500 truncate">{c.role_target || "—"}</div>
                  <div className="col-span-2 text-zinc-500 text-xs truncate">
                    {(c.recruiters || []).map((r) => r.name).filter(Boolean).join(" · ") || "—"}
                  </div>
                  <div className="col-span-1 text-right text-xs">{formatFee(c.fee_approx || 0)}</div>
                  <div className="col-span-2 text-right">
                    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] rounded-sm ${STATUS_STYLE[c.status] || "bg-zinc-100 text-zinc-700"}`}>
                      {c.status}
                    </span>
                  </div>
                </Link>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Interviews timeline */}
      {(client.interviews || []).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3 flex items-center gap-1.5">
            <Calendar size={12} weight="bold" /> Interview history
          </div>
          <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
            {client.interviews.map((iv) => (
              <div key={iv.slot_id} className="px-6 py-3 border-b border-zinc-100 last:border-0 grid grid-cols-12 items-center text-sm gap-2">
                <div className="col-span-3 text-xs text-zinc-500 whitespace-nowrap">
                  {iv.scheduled_at ? new Date(iv.scheduled_at).toLocaleString([], { day: "numeric", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
                </div>
                <Link to={`/candidates/${iv.candidate_id}`} className="col-span-4 font-medium truncate hover:underline">
                  {iv.candidate_name}
                </Link>
                <div className="col-span-3 text-xs text-zinc-500 truncate">
                  {iv.role || "—"} {iv.meeting_with && <>· w/ {iv.meeting_with}</>}
                </div>
                <div className="col-span-2 text-right text-xs">
                  <span className="inline-block px-2 py-0.5 border border-zinc-200 rounded-sm text-zinc-600">
                    {OUTCOME_LABEL[iv.outcome] || "—"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Timeline (bottom of client profile — full engagement history) */}
      <ClientTimelineSection
        events={timeline}
        filter={timelineFilter}
        setFilter={setTimelineFilter}
      />

      {editing && (
        <EditClientModal
          client={client}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); refresh(); }}
        />
      )}
    </div>
  );
}

const StatCard = ({ label, value, accent, small }) => (
  <div className="bg-white border border-zinc-200 rounded-sm p-5">
    <div className={`font-heading font-black ${small ? "text-2xl" : "text-3xl"} tracking-tight ${accent === "emerald" ? "text-emerald-700" : accent === "amber" ? "text-amber-700" : "text-zinc-950"}`}>{value}</div>
    <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mt-1">{label}</div>
  </div>
);

const CLIENT_TIMELINE_TYPES = {
  client_created: { icon: Sparkle, tint: "text-emerald-600 bg-emerald-50 border-emerald-200", label: "Created" },
  interview_booked: { icon: UsersThree, tint: "text-indigo-700 bg-indigo-50 border-indigo-200", label: "Booked" },
  interview_outcome: { icon: CheckCircle, tint: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "Outcome" },
  client_email: { icon: EnvelopeSimple, tint: "text-purple-700 bg-purple-50 border-purple-200", label: "Email" },
};

const CLIENT_TIMELINE_FILTERS = [
  { v: "all", label: "All activity" },
  { v: "interviews", label: "Interviews", types: ["interview_booked", "interview_outcome"] },
  { v: "emails", label: "Emails", types: ["client_email"] },
];

function ClientTimelineSection({ events, filter, setFilter }) {
  const activeFilter = CLIENT_TIMELINE_FILTERS.find((f) => f.v === filter) || CLIENT_TIMELINE_FILTERS[0];
  const filtered = filter === "all"
    ? events
    : events.filter((e) => (activeFilter.types || []).includes(e.type));
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="client-timeline-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Engagement history</div>
          <div className="font-heading font-bold text-xl tracking-tight flex items-center gap-2">
            <ClockCounterClockwise size={18} weight="bold" />
            Timeline
            <span className="text-xs text-zinc-500 font-normal ml-1">
              · {filtered.length} event{filtered.length === 1 ? "" : "s"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {CLIENT_TIMELINE_FILTERS.map((f) => (
            <button
              key={f.v}
              onClick={() => setFilter(f.v)}
              data-testid={`client-timeline-filter-${f.v}`}
              className={`px-2.5 py-1 text-[11px] uppercase tracking-[0.15em] rounded-sm border transition-colors ${
                filter === f.v
                  ? "bg-zinc-950 text-white border-zinc-950"
                  : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-sm text-zinc-500 py-8 text-center border border-dashed border-zinc-200 rounded-sm" data-testid="client-timeline-empty">
          {filter === "all" ? "No activity yet — book an interview or send an email to start the timeline." : "Nothing to show for this filter."}
        </div>
      ) : (
        <div className="relative pl-8">
          <div className="absolute left-3 top-2 bottom-2 w-px bg-zinc-200" aria-hidden />
          <ul className="space-y-4">
            {filtered.map((ev, idx) => {
              const meta = CLIENT_TIMELINE_TYPES[ev.type] || { icon: PaperPlaneTilt, tint: "text-zinc-700 bg-zinc-50 border-zinc-200", label: ev.type };
              const Icon = meta.icon;
              return (
                <li key={`${ev.type}-${ev.at}-${idx}`} className="relative" data-testid={`client-timeline-event-${idx}`}>
                  <div className={`absolute -left-8 top-0.5 w-6 h-6 rounded-full border flex items-center justify-center ${meta.tint}`}>
                    <Icon size={12} weight="bold" />
                  </div>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">
                    <span>{meta.label}</span>
                    <span className="text-zinc-300">·</span>
                    <span title={ev.at || ""}>{_relTime(ev.at)}</span>
                    {ev.actor && (
                      <>
                        <span className="text-zinc-300">·</span>
                        <span className="normal-case tracking-normal text-zinc-500">{ev.actor}</span>
                      </>
                    )}
                  </div>
                  <div className="text-sm font-medium text-zinc-950">{ev.title}</div>
                  {ev.description && (
                    <div className="text-xs text-zinc-600 mt-0.5 whitespace-pre-wrap break-words">
                      {ev.description}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function EditClientModal({ client, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: client.name || "",
    phone: client.phone || "",
    address: client.address || "",
    notes: client.notes || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.patch(`/clients/${client.id}`, form);
      onSaved();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/50 flex items-center justify-center p-4" data-testid="edit-client-modal">
      <div className="bg-white border border-zinc-200 rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-zinc-200">
          <div className="font-heading font-bold text-2xl tracking-tight">Edit client</div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-950"><X size={20} weight="bold" /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4">
          <Field label="Company name" required>
            <input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="edit-client-name" className="w-full bg-white border border-zinc-200 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-zinc-950" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone">
              <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="w-full bg-white border border-zinc-200 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-zinc-950" />
            </Field>
            <Field label="Address">
              <input value={form.address} onChange={(e) => set("address", e.target.value)} className="w-full bg-white border border-zinc-200 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-zinc-950" />
            </Field>
          </div>
          <Field label="Notes">
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={4} data-testid="edit-client-notes" className="w-full bg-white border border-zinc-200 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-950 resize-y" />
          </Field>
          <div className="text-xs text-zinc-500">
            Manage contacts (name, email, title, phone) in the Contacts section on the client page.
          </div>
          {error && <div className="border border-[#FF2A2A] bg-red-50 text-[#B91C1C] text-sm px-3 py-2 rounded-sm">{error}</div>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-zinc-200 bg-white text-zinc-950 py-3 rounded-sm font-medium hover:bg-zinc-100 text-sm">Cancel</button>
            <button type="submit" disabled={submitting} data-testid="save-client-btn" className="flex-1 bg-zinc-950 text-white py-3 rounded-sm font-medium hover:bg-zinc-800 text-sm disabled:opacity-50">
              {submitting ? "Saving..." : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, children, required }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">
      {label}{required && <span className="text-[#FF2A2A]"> *</span>}
    </label>
    {children}
  </div>
);


function ContactsSection({ clientId, contacts, onChange }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");

  const setPrimary = async (ct) => {
    setError("");
    try {
      await api.patch(`/clients/${clientId}/contacts/${ct.id}`, { is_primary: true });
      onChange();
    } catch (err) { setError(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async (ct) => {
    if (!window.confirm(`Remove ${ct.name} from contacts?`)) return;
    setError("");
    try {
      await api.delete(`/clients/${clientId}/contacts/${ct.id}`);
      onChange();
    } catch (err) { setError(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="contacts-section">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1.5">
          <UserCircle size={12} weight="bold" /> Contacts · {contacts.length}
        </div>
        <button
          onClick={() => setShowAdd(true)}
          data-testid="add-contact-btn"
          className="inline-flex items-center gap-1.5 text-xs bg-zinc-950 text-white px-3 py-1.5 rounded-sm hover:bg-zinc-800"
        >
          <Plus size={12} weight="bold" /> Add contact
        </button>
      </div>
      {error && (
        <div className="mb-3 border border-[#FF2A2A] bg-red-50 text-[#B91C1C] px-3 py-2 rounded-sm text-xs">{error}</div>
      )}
      {contacts.length === 0 ? (
        <div className="bg-white border border-zinc-200 border-dashed rounded-sm p-6 text-center text-sm text-zinc-500">
          No contacts yet. Add one and it'll auto-fill interview invites for this client.
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
          {contacts.map((ct) => (
            <div key={ct.id} className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 last:border-0 items-center gap-2 text-sm" data-testid={`contact-row-${ct.id}`}>
              <div className="col-span-3">
                <div className="font-medium flex items-center gap-1.5">
                  {ct.name || "—"}
                  {ct.is_primary && (
                    <span title="Primary contact" className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-[0.15em] text-emerald-800 bg-emerald-100 border border-emerald-300 px-1.5 py-0.5 rounded-sm">
                      <StarFour size={9} weight="fill" /> primary
                    </span>
                  )}
                </div>
                {ct.title && <div className="text-[11px] text-zinc-500 mt-0.5">{ct.title}</div>}
              </div>
              <div className="col-span-3 text-xs text-zinc-600 truncate">{ct.email || "—"}</div>
              <div className="col-span-2 text-xs text-zinc-500">{ct.phone || "—"}</div>
              <div className="col-span-2 text-[11px]" data-testid={`last-emailed-${ct.id}`}>
                {ct.last_emailed ? (
                  <span className="text-zinc-500" title={new Date(ct.last_emailed.at).toLocaleString()}>
                    <span className="text-zinc-950 font-medium">{_relTime(ct.last_emailed.at)}</span>
                    {ct.last_emailed.by && <> via {ct.last_emailed.by}</>}
                  </span>
                ) : (
                  <span className="text-zinc-400">Never emailed</span>
                )}
              </div>
              <div className="col-span-2 flex items-center gap-1 justify-end">
                {!ct.is_primary && (
                  <button onClick={() => setPrimary(ct)} data-testid={`set-primary-${ct.id}`} className="inline-flex items-center gap-1 text-[11px] text-zinc-600 hover:text-zinc-950 px-2 py-1">
                    <Star size={11} weight="bold" /> Set primary
                  </button>
                )}
                <button onClick={() => setEditingId(ct.id)} data-testid={`edit-contact-${ct.id}`} className="inline-flex items-center gap-1 text-[11px] border border-zinc-200 bg-white text-zinc-950 px-2 py-1 rounded-sm hover:bg-zinc-100">
                  <PencilSimple size={11} weight="bold" /> Edit
                </button>
                <button onClick={() => remove(ct)} data-testid={`delete-contact-${ct.id}`} className="inline-flex items-center gap-1 text-[11px] border border-[#FF2A2A] text-[#B91C1C] px-2 py-1 rounded-sm hover:bg-red-50">
                  <Trash size={11} weight="bold" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {showAdd && (
        <ContactModal clientId={clientId} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); onChange(); }} />
      )}
      {editingId && (
        <ContactModal clientId={clientId} contact={contacts.find((c) => c.id === editingId)} onClose={() => setEditingId(null)} onSaved={() => { setEditingId(null); onChange(); }} />
      )}
    </div>
  );
}

function ContactModal({ clientId, contact, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: contact?.name || "",
    email: contact?.email || "",
    phone: contact?.phone || "",
    title: contact?.title || "",
    is_primary: !!contact?.is_primary,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const isEdit = !!contact;
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      if (isEdit) {
        await api.patch(`/clients/${clientId}/contacts/${contact.id}`, form);
      } else {
        await api.post(`/clients/${clientId}/contacts`, form);
      }
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/50 flex items-center justify-center p-4" data-testid="contact-modal">
      <div className="bg-white border border-zinc-200 rounded-sm w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-zinc-200">
          <div className="font-heading font-bold text-2xl tracking-tight">{isEdit ? "Edit contact" : "Add contact"}</div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-950"><X size={20} weight="bold" /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4">
          <Field label="Name" required>
            <input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-name-input" className="w-full bg-white border border-zinc-200 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-zinc-950" autoFocus />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Title / role">
              <input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="contact-title-input" className="w-full bg-white border border-zinc-200 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-zinc-950" placeholder="Head of Sales" />
            </Field>
            <Field label="Phone">
              <input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="contact-phone-input" className="w-full bg-white border border-zinc-200 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-zinc-950" placeholder="+61…" />
            </Field>
          </div>
          <Field label="Email">
            <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email-input" className="w-full bg-white border border-zinc-200 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-zinc-950" placeholder="jane@company.com" />
          </Field>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.is_primary} onChange={(e) => set("is_primary", e.target.checked)} data-testid="contact-primary-toggle" />
            <span>Make this the primary contact</span>
          </label>
          {error && <div className="border border-[#FF2A2A] bg-red-50 text-[#B91C1C] text-sm px-3 py-2 rounded-sm">{error}</div>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-zinc-200 bg-white text-zinc-950 py-3 rounded-sm font-medium hover:bg-zinc-100 text-sm">Cancel</button>
            <button type="submit" disabled={submitting || !form.name} data-testid="save-contact-btn" className="flex-1 bg-zinc-950 text-white py-3 rounded-sm font-medium hover:bg-zinc-800 text-sm disabled:opacity-50">
              {submitting ? "Saving..." : isEdit ? "Save changes" : "Add contact"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
